import { Link } from 'react-router';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import type { IncidentCustomer } from '@models/incidents';
import { formatRigaDateTime } from '@utils/date';

import LoadingAnimation from '@components/LoadingAnimation';
import IncidentDataChangeDialog from '../IncidentDataChangeDialog';
import s from './IncidentCustomersTable.module.css';

const COL_WIDTHS = ['28%', '7%', '8%', '21%', '10%', '11%', '11%', '4%'];

type IncidentCustomersTableProps = {
  isLoading: boolean;
  isFetching: boolean;
  rows: IncidentCustomer[];
  /** Present only for users allowed to write; renders the detach action per row. */
  onDetach?: (row: IncidentCustomer) => void;
};

function IncidentCustomersTable({
  isLoading,
  isFetching,
  rows,
  onDetach,
}: IncidentCustomersTableProps) {
  const showOverlay = isFetching && !isLoading && rows.length > 0;

  return (
    <div className={s.tableShell}>
      <div className={s.tableWrap}>
        <table className={s.table}>
          <colgroup>
            {COL_WIDTHS.map((width, i) => (
              <col key={i} style={{ width }} />
            ))}
          </colgroup>
          <thead>
            <tr>
              <th>Customer</th>
              <th>Brand</th>
              <th>App</th>
              <th>Note</th>
              <th>Source</th>
              <th>Data</th>
              <th>Attached</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={COL_WIDTHS.length} className={s.loadingCell}>
                  <LoadingAnimation compact />
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={COL_WIDTHS.length} className={s.empty}>
                  No customers attached yet.
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.id} className={s.row}>
                  <td className={s.customerCell}>
                    <Link
                      to={`/dashboard/customers/${row.customerId}`}
                      className={s.customerLink}
                      title="Open customer card">
                      <span className={s.email}>{row.email}</span>
                      <OpenInNewIcon sx={{ fontSize: 14 }} aria-hidden />
                    </Link>
                    <div className={s.idMono} title={row.customerId}>
                      {row.customerId}
                    </div>
                  </td>
                  <td>
                    {row.brandCode ? (
                      <span className={s.brandChip}>{row.brandCode.toUpperCase()}</span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className={s.muted}>{row.app}</td>
                  <td className={s.noteCell} title={row.note ?? undefined}>
                    {row.note || '—'}
                  </td>
                  <td>{row.source ? <span className={s.sourceChip}>{row.source}</span> : '—'}</td>
                  <td>
                    <IncidentDataChangeDialog
                      subject={row.email}
                      previousData={row.previousData}
                      newData={row.newData}
                    />
                  </td>
                  <td className={s.muted}>{formatRigaDateTime(row.attachedAt)}</td>
                  <td className={s.actionsCell}>
                    {onDetach && (
                      <button
                        type="button"
                        className={s.detachBtn}
                        onClick={() => onDetach(row)}
                        title="Detach from incident"
                        aria-label={`Detach ${row.email} from incident`}>
                        <LinkOffIcon sx={{ fontSize: 16 }} />
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {showOverlay && (
        <div className={s.fetchingOverlay}>
          <LoadingAnimation compact />
        </div>
      )}
    </div>
  );
}

export default IncidentCustomersTable;
